import React, { useContext, useEffect, useState } from "react";
import {
  PrevButton,
  NextButton,
  usePrevNextButtons,
} from "../global/EmblaCarouselArrowButtons";
import { DotButton, useDotButton } from "../global/EmblaCarouselDotButton";
import { MilestoneBenefitsContext } from "../../context/MilestoneBenefitsContext";
import { MilestonesContext } from "../../context/MilestonesContext";

export default function BrandFeaturesTable({
  currentBrand,
  emblaApi,
  setShowCarousel,
}) {
  const { milestone_benefits, getMilestoneBenefits } = useContext(MilestoneBenefitsContext);
  const { milestones } = useContext(MilestonesContext);
  const [brandLevels, setBrandLevels] = useState([]);

  useEffect(() => {
    if (!milestone_benefits) {
      getMilestoneBenefits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (milestone_benefits && currentBrand && milestones) {
      const brandId = currentBrand.brand_id;
      const sortedMilestones = [...milestones].sort((a, b) => (a.order || 0) - (b.order || 0));
      
      const levels = sortedMilestones
        .filter((milestone) => {
          return milestone_benefits.some(
            (mb) => mb.brand_id === brandId && mb.milestone_id === milestone.milestone_id
          );
        })
        .map((milestone) => {
          const milestoneBenefit = milestone_benefits.find(
            (mb) => mb.brand_id === brandId && mb.milestone_id === milestone.milestone_id
          );
          return {
            number: milestone.milestone_id,
            name: milestone.name || "",
            description: milestoneBenefit.description || "-",
            application: milestoneBenefit.application || "-",
            restrictions: milestoneBenefit.restrictions || "-",
            expiration: milestoneBenefit.expiration || "-",
          };
        });
      
      setBrandLevels(levels);
    }
  }, [milestone_benefits, currentBrand, milestones]);
  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  const renderStars = (amount) => {
    let current = 0;
    const stars = [];

    do {
      stars.push(
        <div
          key={`${current}-${amount}`}
          className="item__star p-0"
          style={{
            width: 30,
            height: 30,
          }}
        />
      );

      current++;
    } while (current < amount);

    return stars;
  };

  const accordionId = `accordion-${currentBrand.instagram.replace(
    /[^a-zA-Z0-9_-]/g,
    ""
  )}`;

  const getLevelName = (level) => {
    if (level.name && level.name.toLowerCase().includes("mes")) {
      return level.name;
    }
    return `NIVEL ${level.number} ${level.name || ""}`;
  };

  // CSS fixed height is 725px for 6 levels
  // Each level row is 56px, so base height = 725 - (6 * 56) = 725 - 336 = 389px
  const rowHeight = 56;
  const headerHeight = 62;
  const baseHeight = 389; // Base height without level rows
  const slideHeight = baseHeight + (brandLevels.length * rowHeight);

  return (
    <div className="brands-features__slide" style={{ minHeight: `${slideHeight}px`, height: `${slideHeight}px` }}>
      <div className="bg-white h-100 slide__container" style={{ minHeight: `${slideHeight}px` }}>
        <div className="row w-100 justify-content-end">
          <button
            type="button"
            className="btn-close"
            onClick={() => setShowCarousel(false)}
          ></button>
        </div>

        <div className="row flex-column align-items-center">
          <h2 className="title font-dm-sans">BENEFICIOS</h2>
          {currentBrand?.file?.src ? (
            <img
              alt="reward brand"
              src={currentBrand.file.src}
              className="w-auto mb-2"
              style={{
                maxHeight: "100px",
              }}
            />
          ) : (
            <div style={{ height: "83px" }}></div>
          )}

          <span
            onClick={() => {
              if (currentBrand?.instagram) {
                const instagramCleaned = currentBrand.instagram.replace(
                  /@/g,
                  ""
                );
                window.open(
                  `https://www.instagram.com/${instagramCleaned}`,
                  "_blank"
                );
              }
            }}
            className="instagram font-dm-sans w-max-content cursor-pointer mb-3"
          >
            <i className="fab fa-instagram me-2 text-muted" />
            {currentBrand?.instagram}
          </span>
          {currentBrand.website && (
            <a
              target="_blank"
              href={currentBrand.website}
              rel="noopener noreferrer"
              className="instagram font-dm-sans w-max-content cursor-pointer mb-3 no-decoration"
            >
              <i className="fas fa-globe me-2 text-muted" />
              {currentBrand?.website.replace("https://", "")}
            </a>
          )}
        </div>

        <div className="row w-100 mx-auto hide-mobile justify-content-center">
          <div
            className="px-0 purple-border-right"
            style={{ width: "325px" }}
          >
            {/* Header row for left column */}
            <div
              className="row w-100 align-items-center mx-auto gray-border-bottom"
              style={{
                height: `${headerHeight}px`,
              }}
            ></div>
            {/* Data rows for left column */}
            {brandLevels.map((level, index) => (
              <div
                key={level.number + index}
                className={`row w-100 align-items-center ps-2 mx-auto `}
                style={{
                  height: `${rowHeight}px`,
                  backgroundColor: [0, 2, 4].includes(index) ? "#F2F2F2" : "",
                }}
              >
                <span className="dm-sans-font me-2 p-0 w-max-content level">
                  {getLevelName(level)}
                </span>
                {level.name && !level.name.toLowerCase().includes("mes") && renderStars(level.number)}
              </div>
            ))}
          </div>
          <div
            className="col-8 px-0"
            style={{ width: "660px" }}
          >
            {/* Header row for right column */}
            <div
              className="row w-100 align-items-center mx-auto gray-border-bottom"
              style={{
                height: `${headerHeight}px`,
              }}
            >
              <div className="col text-center d-flex align-items-center justify-content-center">
                <p className="feat font-dm-sans my-auto w-max-content">
                  BENEFICIOS
                </p>
              </div>
              <div className="col text-center d-flex align-items-center justify-content-center">
                <p className="feat font-dm-sans my-auto w-max-content">
                  APLICACIÓN
                </p>
              </div>
              <div className="col text-center d-flex align-items-center justify-content-center">
                <p className="feat font-dm-sans my-auto w-max-content">
                  RESTRICCIONES
                </p>
              </div>
              <div className="col text-center d-flex align-items-center justify-content-center">
                <p className="feat font-dm-sans my-auto w-max-content">
                  VIGENCIA
                </p>
              </div>
            </div>
            {/* Data rows for right column */}
            {brandLevels.map((level, index) => (
              <div
                key={level.number + index}
                className={`row w-100 align-items-center ps-2 mx-auto `}
                style={{
                  height: `${rowHeight}px`,
                  backgroundColor: [0, 2, 4].includes(index) ? "#F2F2F2" : "",
                }}
              >
                <div className="col text-center feat-content font-dm-sans">
                  {level?.description ? level.description : "-"}
                </div>
                <div className="col text-center feat-content font-dm-sans">
                  {level?.application ? level.application : "-"}
                </div>
                <div className="col text-center feat-content font-dm-sans">
                  {level?.restrictions ? level.restrictions : "-"}
                </div>
                <div className="col text-center feat-content font-dm-sans">
                  {level?.expiration ? level.expiration : "-"}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="row w-100 mx-auto show-mobile">
          <div id={accordionId} className="accordion accordion-brands bg-white">
            {brandLevels.map((level, index) => {
              const levelId = `collapseLevel-${currentBrand.instagram.replace(
                /[^a-zA-Z0-9_-]/g,
                ""
              )}-${level.number}`;
              return (
                <div
                  className="accordion-item border rounded mb-2 "
                  key={level.number + index}
                >
                  <h2 className="accordion-header bg-transparent">
                    <button
                      className="accordion-button bg-transparent collapsed py-2 px-3"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${levelId}`}
                      aria-expanded="false"
                      aria-controls={levelId}
                    >
                      <h2 className="bold item__title me-2">
                        {getLevelName(level)}
                      </h2>
                      {level.name && !level.name.toLowerCase().includes("mes") && renderStars(level.number)}
                    </button>
                  </h2>
                  <div
                    id={levelId}
                    className="accordion-collapse collapse"
                    data-bs-parent={`#${accordionId}`}
                  >
                    <div className="accordion-body small px-3 py-2">
                      <p className="mb-1 " style={{ fontSize: "12px" }}>
                        <strong style={{ fontSize: "15px" }}>Beneficio:</strong>{" "}
                        {level.description}
                      </p>
                      <p className="mb-1 " style={{ fontSize: "12px" }}>
                        <strong style={{ fontSize: "15px" }}>
                          Aplicación:
                        </strong>{" "}
                        {level.application}
                      </p>
                      <p className="mb-1 " style={{ fontSize: "12px" }}>
                        <strong style={{ fontSize: "15px" }}>
                          Restricciones:
                        </strong>{" "}
                        {level.restrictions}
                      </p>
                      <p className="mb-0 " style={{ fontSize: "12px" }}>
                        <strong style={{ fontSize: "15px" }}>Vigencia:</strong>{" "}
                        {level.expiration}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div 
          className="brands-features__buttons"
          style={{
            top: `calc((${slideHeight}px / 2)`,
          }}
        >
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        </div>

        <div className="brands-features__controls">
          <div className="brands-features__dots">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={"brands-features__dot".concat(
                  index === selectedIndex
                    ? " brands-features__dot--selected"
                    : ""
                )}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
